// Miky Motorsport - dynamicka chase kamera (FOV dle rychlosti, pruzinovy follow)
import * as THREE from 'three';
import { CAMERA, CAMERA_VIEW, CAMERA_VIEW_ORDER, MS_TO_KMH } from './constants.js';
import { targetFovFor, dampFactor, cameraRoll, lookAheadLateral } from './cameraMath.js';

export class ChaseCamera {
  constructor(aspect) {
    this.cam = new THREE.PerspectiveCamera(CAMERA.FOV_BASE, aspect, 0.1, 1400);
    this._viewIdx = 0;
    this.view = CAMERA_VIEW_ORDER[0];

    this._camPos = new THREE.Vector3(0, 5, -10);
    this._lookAt = new THREE.Vector3();
    this._desiredPos = new THREE.Vector3();
    this._desiredLook = new THREE.Vector3();
    this._fwd = new THREE.Vector3();
    this._up = new THREE.Vector3();
    this._right = new THREE.Vector3();
    this._off = new THREE.Vector3();
    this._lookOff = new THREE.Vector3();
    this._fov = CAMERA.FOV_BASE;
    this._fovPunch = 0; // prechodne stazeni FOV (gear-shift/near-miss), tlumi se k 0
    this._kick = new THREE.Vector3(); // smerovy recoil kamery (naraz), tlumi se k 0
    this._inited = false;
    this._shake = 0;
  }

  shake(amt) { this._shake = Math.min(0.8, this._shake + amt); }

  // Prechodne stazeni/rozsireni FOV (gear-shift squeeze = zaporne, near-miss spike = kladne).
  punchFov(deg) { this._fovPunch += deg; }

  // Smerovy recoil kamery pri narazu. dir = svetovy smer (Vector3), amt = sila (m).
  kick(dir, amt) { this._kick.addScaledVector(dir, amt); }

  cycleView() {
    this._viewIdx = (this._viewIdx + 1) % CAMERA_VIEW_ORDER.length;
    this.view = CAMERA_VIEW_ORDER[this._viewIdx];
    this._inited = false; // skok bez interpolace pri zmene pohledu
  }

  resize(aspect) {
    this.cam.aspect = aspect;
    this.cam.updateProjectionMatrix();
  }

  // skok bez interpolace (pri startu zavodu / zmene rezimu)
  snap() {
    this._inited = false;
  }

  // pos: THREE.Vector3, quat: THREE.Quaternion (chassis), speedMs: number, dt: s, steer: -1..1
  update(pos, quat, speedMs, dt, steer = 0) {
    this._fwd.set(0, 0, 1).applyQuaternion(quat); // dopredu (+z local)
    this._up.set(0, 1, 0).applyQuaternion(quat);
    this._right.set(1, 0, 0).applyQuaternion(quat); // vpravo (+x local)
    const speedKmh = Math.abs(speedMs) * MS_TO_KMH;

    if (this.view === CAMERA_VIEW.CHASE) {
      // odstup roste s rychlosti (pull-back = vetsi pocit rychlosti)
      const back = targetFovFor(speedKmh, CAMERA.CHASE_BACK, CAMERA.CHASE_BACK_MAX, CAMERA.DIST_SPEED_KMH);
      this._off.copy(this._fwd).multiplyScalar(-back);
      this._desiredPos.copy(pos).add(this._off);
      this._desiredPos.y += CAMERA.CHASE_HEIGHT;
      // look-ahead + bocni posun do zatacky (kouka "do" zatacky)
      const lat = lookAheadLateral(steer, speedKmh, CAMERA.LOOK_LATERAL, CAMERA.LEAN_REF_KMH);
      this._desiredLook
        .copy(pos)
        .addScaledVector(this._fwd, CAMERA.CHASE_LOOK_AHEAD)
        .addScaledVector(this._right, lat)
        .add(this._lookOff.set(0, 0.8, 0));
    } else if (this.view === CAMERA_VIEW.HOOD) {
      this._desiredPos.copy(pos).addScaledVector(this._fwd, 0.4).addScaledVector(this._up, 0.95);
      this._desiredLook.copy(pos).addScaledVector(this._fwd, 12).addScaledVector(this._up, 0.7);
    } else {
      // BUMPER
      this._desiredPos.copy(pos).addScaledVector(this._fwd, 1.9).addScaledVector(this._up, 0.35);
      this._desiredLook.copy(pos).addScaledVector(this._fwd, 14).addScaledVector(this._up, 0.3);
    }

    if (!this._inited) {
      this._camPos.copy(this._desiredPos);
      this._lookAt.copy(this._desiredLook);
      this._inited = true;
    } else {
      // damped (frame-rate nezavisly) lerp
      const stiff = this.view === CAMERA_VIEW.CHASE ? CAMERA.FOLLOW_STIFFNESS : 22;
      const a = dampFactor(stiff, dt);
      this._camPos.lerp(this._desiredPos, a);
      this._lookAt.lerp(this._desiredLook, Math.min(1, a * 1.4));
    }

    this.cam.position.copy(this._camPos);
    // smerovy recoil (naraz) - tlumi se k nule
    if (this._kick.lengthSq() > 1e-5) {
      this.cam.position.add(this._kick);
      this._kick.multiplyScalar(Math.exp(-12 * dt));
    } else { this._kick.set(0, 0, 0); }
    if (this._shake > 0.002) {
      this.cam.position.x += (Math.random() - 0.5) * this._shake;
      this.cam.position.y += (Math.random() - 0.5) * this._shake * 0.6;
      this.cam.position.z += (Math.random() - 0.5) * this._shake;
      this._shake *= Math.exp(-7 * dt);
    }
    this.cam.lookAt(this._lookAt);
    // naklon kamery do zatacky (jen chase) - az po lookAt, aby nakloni horizont
    if (this.view === CAMERA_VIEW.CHASE) {
      const roll = cameraRoll(steer, speedKmh, CAMERA.LEAN_MAX_RAD, CAMERA.LEAN_REF_KMH);
      if (roll) this.cam.rotateZ(roll);
    }

    // FOV roste s rychlosti (pocit rychlosti) + prechodny punch (gear-shift/near-miss)
    const targetFov = targetFovFor(speedKmh, CAMERA.FOV_BASE, CAMERA.FOV_MAX, CAMERA.FOV_SPEED_KMH);
    this._fov += (targetFov - this._fov) * Math.min(1, 4 * dt);
    this._fovPunch = Math.abs(this._fovPunch) > 0.01 ? this._fovPunch * Math.exp(-9 * dt) : 0;
    const fov = this._fov + this._fovPunch;
    if (Math.abs(fov - this.cam.fov) > 0.01) {
      this.cam.fov = fov;
      this.cam.updateProjectionMatrix();
    }
  }
}
